import {
    getSelfcare360DimensionTier,
    getWeightPercent,
    roundHalfUp,
} from '../../components/selfcare360/selfcare360Scales';

export const IPRA_INTERVENTION_PAGE_LIMIT = 4;

export const IPRA_TIER_META = {
    critical: {
        label: 'Riesgo crítico',
        badgeLabel: 'Riesgo Crítico',
        priorityLabel: 'Muy Alta',
        timeframe: '0–15 días',
        bgColor: '#FEF2F2',
        textColor: '#991B1B',
        borderColor: '#FECACA',
    },
    high: {
        label: 'Riesgo alto',
        badgeLabel: 'Riesgo Alto',
        priorityLabel: 'Alta',
        timeframe: '15–30 días',
        bgColor: '#FFF7ED',
        textColor: '#C2410C',
        borderColor: '#FDBA74',
    },
    moderate: {
        label: 'Riesgo moderado',
        badgeLabel: 'Riesgo Moderado',
        priorityLabel: 'Moderada',
        timeframe: '30–60 días',
        bgColor: '#FFFBEB',
        textColor: '#B45309',
        borderColor: '#FDE68A',
    },
    low: {
        label: 'Riesgo bajo',
        badgeLabel: 'Riesgo Bajo',
        priorityLabel: 'Mantener',
        timeframe: 'Sostenimiento',
        bgColor: '#ECFDF5',
        textColor: '#166534',
        borderColor: '#86EFAC',
    },
};

const IPRA_DIMENSION_CATALOG = {
    D1: {
        summaryTitle: 'Controles críticos',
        planTitle: 'Controles Críticos y Condiciones del Área',
        keywords: ['controles criticos', 'condiciones del area'],
    },
    D2: {
        summaryTitle: 'Planeación segura',
        planTitle: 'Planeación y Arranque Seguro del Trabajo',
        keywords: ['planeacion segura', 'arranque seguro', 'ats'],
    },
    D3: {
        summaryTitle: 'Cumplimiento reglas',
        planTitle: 'Cumplimiento y Exigencia de Reglas',
        keywords: ['cumplimiento reglas', 'exigencia de reglas'],
    },
    D4: {
        summaryTitle: 'Stop Work',
        planTitle: 'Detención de Trabajos Inseguros (Stop Work)',
        keywords: ['stop work', 'detencion de trabajos inseguros'],
    },
    D5: {
        summaryTitle: 'Aprendizaje incidentes',
        planTitle: 'Aprendizaje de Incidentes y Casi Accidentes',
        keywords: ['aprendizaje de incidentes', 'casi accidentes'],
    },
    D6: {
        summaryTitle: 'Participación equipo',
        planTitle: 'Participación y Clima de Seguridad',
        keywords: ['participacion del equipo', 'participacion equipo', 'clima de seguridad'],
    },
    D7: {
        summaryTitle: 'Liderazgo operativo',
        planTitle: 'Liderazgo Visible y Coherente en Seguridad',
        keywords: ['liderazgo operativo', 'liderazgo visible'],
    },
    D8: {
        summaryTitle: 'Gestión de fatiga',
        planTitle: 'Gestión de Fatiga, Carga y Factores Humanos',
        keywords: ['gestion de fatiga', 'fatiga', 'factores humanos'],
    },
};

const INTERVENTION_PLANS = {
    D1: {
        critical: {
            supervisor: [
                'Realiza un recorrido físico de tu área esta semana e identifica todas las condiciones inseguras activas — documenta cada una con foto, responsable y fecha de cierre.',
                'Detén y corrige en el momento cualquier tarea con controles críticos ausentes: guardas retiradas, energía no bloqueada, condiciones físicas inseguras.',
                'Implementa un chequeo personal diario de los 5 puntos de mayor riesgo de tu área antes de iniciar el turno — esto no puede delegarse.',
                'Reporta a tu jefe inmediato cualquier condición que no puedas resolver en tu turno — nunca la dejes sin escalar.',
                'Modela el comportamiento: usa EPP completo en todo momento. Tu equipo replica lo que te ve hacer.',
            ],
            manager: [
                'Realiza un recorrido conjunto con el supervisor esta semana — observa qué condiciones han sido ignoradas y retroalimenta en campo, sin señalamiento público.',
                'Pregunta en cada reunión 1:1: ¿Cuántas condiciones inseguras detectaste? ¿Cuántas cerraste? Un número bajo de detecciones indica un problema de cultura, no de condiciones.',
                'Verifica que el supervisor tenga autoridad y recursos para cerrar condiciones sin depender de aprobaciones lentas que generan riesgo por mora.',
                'Establece con el supervisor una meta de cierre de condiciones a 15 días con revisión semanal.',
            ],
            system: [
                'Auditoría técnica de toda el área: máquinas, guardas, bloqueos, señalización y EPP disponible — en los próximos 7 días con HSE.',
                'Revisar si existen condiciones que llevan semanas sin cerrarse por falta de presupuesto o autorización — escalarlas a gerencia.',
                'Implementar indicador de condiciones inseguras detectadas vs cerradas, reportado semanalmente al gerente del área.',
                'Verificar que el estándar LOTO (Lockout/Tagout) esté documentado, publicado y conocido por todos los supervisores.',
            ],
            kpi: [
                '# condiciones inseguras detectadas / cerradas por turno',
                '% puntos críticos verificados',
                'Tiempo promedio de cierre de condiciones',
                '# EPP no conformes identificados',
            ],
        },
        high: {
            supervisor: [
                'Establece una rutina de recorridos de seguridad semanales con registro fotográfico y acciones tomadas — compártela con tu jefe.',
                'Cuando detectes una condición insegura, corrígela en el momento o asigna responsable con fecha de cierre ese mismo día.',
                'Capacita a tu equipo en la verificación de condiciones previas al trabajo — multiplica tus ojos en el área.',
                'Revisa que los procedimientos de inspección preoperacional sean conocidos y estén siendo usados, no solo firmados.',
            ],
            manager: [
                'Revisión semanal del registro de condiciones del área — pregunta qué acciones tomó el supervisor, no solo cuántas detectó.',
                'Acompañar al supervisor en al menos un recorrido mensual para observar su estilo de identificación y corrección.',
            ],
            system: [
                'Programar mantenimiento preventivo con mayor frecuencia en equipos con más fallas.',
                'Incorporar el control de condiciones críticas como indicador en el tablero mensual del área.',
            ],
            kpi: [
                '% inspecciones preoperacionales completadas',
                '# condiciones detectadas / cerradas por semana',
            ],
        },
    },
    D2: {
        critical: {
            supervisor: [
                'Implementa una reunión pre-turno de 10 minutos obligatoria antes de cada turno — identifica los 3 riesgos del día con tu equipo, documenta y firma.',
                'Nunca inicies ni autorices una tarea de alto riesgo sin haber realizado un Análisis de Tarea Segura (ATS) — aunque haya presión de tiempo.',
                'Desarrolla esta semana el ATS para las 5 tareas más frecuentes y peligrosas de tu área — son la base del arranque seguro.',
                'Cuando cambien las condiciones del turno (falla de máquina, personal nuevo, tarea no programada), detén el trabajo, revisa los riesgos y decide — no improvises.',
                'Si recibes presión de producción para saltarte la planeación, escala a tu jefe — esa presión no puede recaer solo sobre ti.',
            ],
            manager: [
                'Observa las reuniones pre-turno del supervisor durante 2 semanas — ¿se realizan? ¿tienen contenido real de seguridad o son un trámite?',
                'Conversación directa: ¿Qué te impide hacer la planeación completa antes de iniciar? ¿Presión de tiempo? ¿Recursos? ¿Falta de metodología? Identifica la causa real.',
                'Garantiza que el supervisor tenga el tiempo formal y la autoridad para detener un arranque si las condiciones no están verificadas.',
                'Revisión mensual de permisos de trabajo — ¿se cumplen los controles previstos o se firma de forma automática?',
            ],
            system: [
                'Implementar formato estructurado de ATS para las tareas críticas del área — disponible en cada puesto de trabajo.',
                'Revisar si la carga operativa permite realmente el tiempo de planeación o si la organización genera la presión que omite este paso.',
                'Auditoría de permisos de trabajo de los últimos 30 días — verificar calidad real del análisis de riesgo vs cumplimiento formal.',
                'Definir política de tiempo mínimo de planeación para tareas críticas y protegerlo de presiones de producción.',
            ],
            kpi: [
                '% turnos con reunión pre-turno documentada',
                '% tareas críticas con ATS',
                '# arranques detenidos por condiciones inseguras',
            ],
        },
        high: {
            supervisor: [
                'Consolida la reunión pre-turno como rutina diaria inquebrantable — si un día la omites, identifica por qué y corrígelo.',
                'Practica el ATS en campo con tu equipo para las tareas de mayor riesgo.',
                'Ante cualquier tarea no habitual, detente, pregunta, analiza — así sea 5 minutos. Ese tiempo evita incidentes.',
            ],
            manager: [
                'Acompañar al supervisor en un recorrido de arranque de turno para observar cómo gestiona la planeación.',
                'Retroalimentar sobre calidad del ATS — ¿identifica causas raíz o solo causas inmediatas?',
            ],
            system: [
                'Simplificar el ATS para que sea práctico en campo, no un formato burocrático.',
                'Incorporar la planeación segura como indicador en el sistema de gestión del área.',
            ],
            kpi: [
                '% turnos con pre-turno estructurado',
                '# ATS documentados por semana',
            ],
        },
    },
    D3: {
        critical: {
            supervisor: [
                'Identifica esta semana las 3 normas de seguridad que más se incumplen en tu área — no las que más se reportan, las que realmente se incumplen.',
                'Ante el próximo incumplimiento que observes, corrígelo en campo, en el momento, con respeto — sin esperar la reunión ni el reporte.',
                'Revisa si eres coherente: ¿exiges al equipo normas que tú mismo no cumples? Eso destruye la credibilidad y el cumplimiento.',
                'Implementa observaciones de conducta en campo (BBS) — mínimo 2 por turno — con retroalimentación positiva al final.',
                'Nunca cedas al incumplimiento por presión de producción — cuando el equipo te vea ceder una vez, el estándar colapsa.',
            ],
            manager: [
                'Observación de campo con el supervisor esta semana — ¿cómo responde ante un incumplimiento real? ¿corrige en el momento o lo deja pasar?',
                'Conversación directa: ¿Qué barreras tiene para exigir el cumplimiento de normas? ¿Miedo al conflicto? ¿Presión del equipo? ¿Normas poco claras?',
                'Verifica que las consecuencias por incumplimiento sean claras, conocidas y se apliquen consistentemente — sin esto el supervisor no puede sostener el estándar.',
                'Revisión mensual: ¿La tasa de incumplimientos está bajando? ¿La tasa de correcciones respetuosas está subiendo?',
            ],
            system: [
                'Auditoría del sistema de consecuencias: ¿existen, son conocidas, se aplican consistentemente en todos los niveles?',
                'Revisar si las normas del área están actualizadas, son comprensibles y están accesibles en el punto de trabajo.',
                'Implementar BBS (Behavior Based Safety) como metodología formal del área con indicadores semanales.',
                'Garantizar que el supervisor no reciba señales contradictorias: que producción no premie el incumplimiento cuando acelera la meta.',
            ],
            kpi: [
                '# observaciones BBS realizadas',
                '% correcciones con retroalimentación en el momento',
                '% reducción de desviaciones registradas vs período anterior',
            ],
        },
        high: {
            supervisor: [
                "Practica corregir sin señalar: 'Vi algo que me preocupa, hablemos antes de continuar' — haz de esto un hábito de comunicación.",
                'Analiza en tu reunión de turno los incumplimientos más frecuentes del período — busca causas sistémicas, no culpables personales.',
                'Verifica que el EPP esté disponible, en buen estado y accesible — a veces el incumplimiento tiene causa logística, no actitudinal.',
            ],
            manager: [
                'Acompañar al supervisor en una observación de campo y retroalimentar su estilo de corrección — ¿es constructivo o punitivo?',
                'Revisar si la tasa de reportes de incumplimiento ha aumentado (buen signo) o hay silencio (señal de alarma).',
            ],
            system: [
                'Asegurar que EPP y herramientas estén siempre disponibles y en buen estado — sin esto el incumplimiento tiene causa estructural.',
                'Incorporar BBS como indicador de gestión del área.',
            ],
            kpi: [
                '# observaciones BBS',
                '% cumplimiento EPP en recorridos',
                '# incumplimientos por incidente',
            ],
        },
    },
    D4: {
        critical: {
            supervisor: [
                'Esta semana, detén un trabajo en tu área ante el primer riesgo real que identifiques — aunque no sea una emergencia. Que tu equipo vea que lo haces.',
                'Comunica formalmente a tu equipo que cualquier persona puede detener el trabajo por seguridad sin necesidad de tu autorización — y que tendrá tu respaldo.',
                'Identifica si en el último mes hubo situaciones donde el trabajo debió detenerse y no se hizo — analiza qué impidió la parada.',
                "Practica la comunicación del Stop Work: 'Detengo este trabajo porque...' — simple, directa, sin disculpas.",
                'Si sientes presión de producción para no detener un trabajo inseguro, escala inmediatamente a tu jefe — esa presión no puede recaer solo sobre ti.',
            ],
            manager: [
                'Reunión inmediata con el supervisor: ¿Ha habido situaciones en que sintió que no podía parar el trabajo por presión del sistema? Escuchar sin defensas.',
                'Revisar si existe algún antecedente donde un Stop Work fue sancionado o generó consecuencias negativas — ese antecedente debe ser corregido públicamente.',
                'Emitir comunicado formal al equipo y al supervisor reafirmando que el Stop Work es un acto de liderazgo respaldado por la organización.',
                'Acompañar al supervisor en el próximo simulacro de Stop Work — tu presencia como jefe envía un mensaje claro.',
            ],
            system: [
                'Comunicado oficial de gerencia reafirmando el derecho y la obligación de Stop Work para todos los niveles.',
                'Revisar y corregir cualquier caso histórico donde el Stop Work generó consecuencias negativas para quien lo ejerció.',
                'Implementar indicador de # Stop Work ejercidos por período — un número bajo en áreas de alto riesgo es señal de miedo, no de seguridad.',
                'Incluir el ejercicio del Stop Work como criterio positivo en la evaluación de desempeño de supervisores y operadores.',
            ],
            kpi: [
                '# Stop Work ejercidos por el supervisor',
                '# paradas validadas por HSE',
                '% del equipo que reporta sentirse respaldado para parar',
                '# incidentes en tareas que debieron detenerse',
            ],
        },
        high: {
            supervisor: [
                'Realiza un role-playing con tu equipo: situaciones reales del área donde el Stop Work aplica — practica la decisión en un ambiente sin presión.',
                'Analiza el último mes: ¿cuántas paradas de trabajo por seguridad realizaste o respaldaste? ¿Cuántas debieron ocurrir y no ocurrieron?',
                'Publica el protocolo de Stop Work en tu área de trabajo — que todos sepan cómo se ejerce y cuáles son los pasos.',
            ],
            manager: [
                'Reconocer públicamente al supervisor la primera vez que ejerza o respalde un Stop Work.',
                'Revisión mensual: ¿Los Stop Work se resuelven rápido y el trabajo se retoma con condiciones seguras?',
            ],
            system: [
                'Protocolo de Stop Work documentado, publicado y comprensible para todo el personal del área.',
                'Incluir Stop Work en la próxima capacitación de seguridad del área.',
            ],
            kpi: [
                '# Stop Work ejercidos',
                'Tiempo promedio de resolución de paradas',
                '% equipo que conoce el protocolo',
            ],
        },
    },
    D5: {
        critical: {
            supervisor: [
                'Revisa todos los reportes de incidentes y casi-accidentes de tu área de los últimos 3 meses — ¿cuántos tienen análisis de causa raíz real? ¿Cuántas acciones de mejora están cerradas?',
                'Ante el próximo casi-accidente o incidente en tu área, lidera tú mismo la investigación — no la delegues hasta construir el hábito.',
                'Usa la técnica de los 5 Por Qués en la próxima investigación — busca causas sistémicas, no personas culpables.',
                'En tu reunión de turno de esta semana, comparte una lección aprendida de un incidente reciente — aunque sea de otra área.',
                'Verifica que los casi-accidentes se reporten sin miedo — si tu equipo no reporta, el sistema puede estar castigando quien lo hace.',
            ],
            manager: [
                'Revisión inmediata de la tasa de cierre de acciones de mejora en el área del supervisor — si está por debajo del 70%, es un problema sistémico.',
                'Observar cómo el supervisor conduce una investigación — ¿busca causas o busca culpables? Retroalimentar en privado.',
                'Pregunta mensual en 1:1: ¿Cuántos casi-accidentes reportó tu equipo esta semana? Si la respuesta es sistemáticamente cero, hay un problema de cultura.',
                'Garantizar que el supervisor tenga tiempo y recursos para cerrar acciones de mejora — no solo para identificarlas.',
            ],
            system: [
                'Auditoría del sistema de gestión de incidentes: tasa de reporte, calidad de análisis de causa raíz, tasa de cierre de acciones.',
                'Simplificar el proceso de reporte de casi-accidentes — si tiene más de 3 pasos, ya es una barrera.',
                'Definir KPI de % acciones de mejora cerradas en plazo — reportado mensualmente a gerencia.',
                'Revisar si existen patrones de incidentes repetitivos que no han sido abordados a nivel sistémico.',
            ],
            kpi: [
                '% acciones de mejora cerradas en plazo',
                '# casi-accidentes reportados por semana',
                '# incidentes con causa raíz sistémica identificada',
                '# lecciones aprendidas compartidas',
            ],
        },
        high: {
            supervisor: [
                'Capacítate en técnicas de análisis de causa raíz: 5 Por Qués, Árbol de Causas — elige la que mejor aplica a tu área.',
                'Implementa seguimiento semanal de todas las acciones de mejora pendientes — con nombre y fecha de cierre visible.',
                'Comparte al menos una lección aprendida por semana en tu reunión de turno.',
            ],
            manager: [
                "Revisar calidad de los últimos 5 análisis de causa raíz del área — ¿se llega a causas sistémicas o siempre se concluye 'error humano'?",
                'Acompañar al supervisor en la investigación de un casi-accidente para modelar el proceso correcto.',
            ],
            system: [
                'Revisar si la metodología de investigación de incidentes es conocida y accesible para todos los supervisores.',
                'Incorporar tasa de cierre de acciones en el tablero de gestión del área.',
            ],
            kpi: [
                '% acciones cerradas en plazo',
                '# casi-accidentes reportados',
                'Calidad de análisis de causa raíz',
            ],
        },
    },
    D6: {
        critical: {
            supervisor: [
                'Aplica una conversación individual corta (5 min) con cada persona de tu equipo esta semana — pregunta: ¿Hay algo en la seguridad del área que no te hayas atrevido a decirme? Escucha sin defenderte.',
                'Implementa un mecanismo simple de reporte de riesgos accesible para todos los turnos — puede ser un cuaderno, un formato simple o un QR. Lo importante es que exista y que tú respondas.',
                'Ante el próximo reporte de riesgo que recibas, da una respuesta visible antes de 72 horas — aunque sea para decir qué está pendiente y cuándo se cierra.',
                'Crea un espacio en tu reunión de turno donde el equipo pueda plantear preocupaciones sin jerarquía — solo escuchas, no te defiendes ni contraatacas.',
                'Si alguien reporta un riesgo y no recibe respuesta, ese silencio destruye la cultura de reporte de toda el área.',
            ],
            manager: [
                'Revisión inmediata: ¿Cuántos reportes de riesgo ha recibido el área del supervisor en los últimos 3 meses? ¿Cuántos tuvieron respuesta visible?',
                'Pregunta directa al supervisor: ¿Siente que puede hablar contigo sobre problemas de seguridad sin consecuencias? Si la respuesta duda, hay trabajo hacia arriba también.',
                'Facilitar un diagnóstico de clima de seguridad en el área — los números revelarán lo que las conversaciones no muestran.',
                'Garantizar que el supervisor reciba soporte para responder reportes — si no tiene recursos, eso bloquea la cultura de participación.',
            ],
            system: [
                'Diagnóstico de clima de seguridad con herramienta validada — en los próximos 15 días.',
                'Canal de reporte de riesgos anónimo (buzón o digital) con compromiso de respuesta documentada en 72 horas.',
                'Indicador de tasa de respuesta a reportes de riesgo — reportado mensualmente a gerencia.',
                'Revisar si el comité paritario de seguridad tiene representación real de los turnos operativos y si sus conclusiones tienen seguimiento efectivo.',
            ],
            kpi: [
                '# reportes de riesgo recibidos',
                '% reportes con respuesta visible ≤72h',
                '# personas que participaron en espacios de seguridad',
                'Resultado encuesta de clima de seguridad',
            ],
        },
        high: {
            supervisor: [
                'Activa los canales de reporte que ya existen — si no los usa tu equipo, pregunta por qué y elimina las barreras.',
                'Reconoce públicamente en la reunión de turno a quien reporte un riesgo — el reconocimiento visible genera más reportes.',
                "Cuando cierres un reporte, comunícalo al equipo: 'Reportaste esto, hicimos esto' — el ciclo completo construye confianza.",
            ],
            manager: [
                'Acompañar al supervisor en la revisión de reportes de riesgo recientes — ¿se cierran con solución real o con respuestas formales?',
                'Reconocer públicamente al supervisor cuando la tasa de reporte crece.',
            ],
            system: [
                'Verificar que los mecanismos de participación existentes funcionen en la práctica, no solo en el papel.',
                'Incorporar tasa de reporte de riesgos en el tablero de seguridad del área.',
            ],
            kpi: [
                '# reportes de riesgo',
                '% respuestas en 72h',
                '# personas que participaron en actividades de seguridad',
            ],
        },
    },
    D7: {
        critical: {
            supervisor: [
                "Esta semana: ¿Cumples tú mismo las normas de seguridad que exiges a tu equipo? Si la respuesta es 'no siempre', empieza ahí — sin excepciones.",
                'Realiza al menos 3 recorridos de campo en tu área esta semana — no para supervisar, para estar presente y visible.',
                'Identifica una decisión reciente donde priorizaste la producción sobre la seguridad — reconócela ante tu equipo. La coherencia empieza con la honestidad.',
                'Cuando la meta de producción entre en conflicto con la seguridad esta semana, elige la seguridad y comunica por qué — que tu equipo lo vea.',
                'Solicita a tu jefe un proceso de coaching en liderazgo en seguridad — esta dimensión requiere acompañamiento externo, no solo voluntad.',
            ],
            manager: [
                'Proceso de evaluación 360° del liderazgo en seguridad de este supervisor — confidencial, con resultados compartidos solo con el supervisor y su jefe.',
                'Coaching o mentoría estructurada en liderazgo en seguridad — mínimo 4 sesiones en los próximos 2 meses.',
                'Observación de campo mensual: ¿El supervisor hace lo que dice? ¿O hay una brecha entre su discurso y su comportamiento? Retroalimentar en privado.',
                'Revisar si la presión de producción que el supervisor recibe del sistema lo está empujando a decisiones poco coherentes — ese problema es sistémico, no individual.',
            ],
            system: [
                'Incluir coherencia liderazgo-seguridad como competencia evaluada en la evaluación de desempeño del supervisor.',
                'Programa de coaching en seguridad para todos los supervisores — no solo como reacción sino como desarrollo.',
                'Revisar si la organización está enviando mensajes contradictorios: que la producción prima sobre la seguridad a nivel directivo.',
                'Indicador de # recorridos de campo por supervisor por semana — visible en tablero de gestión.',
            ],
            kpi: [
                '# recorridos de campo documentados',
                'Resultado 360° de liderazgo en seguridad',
                '# decisiones seguridad vs producción a favor de seguridad',
                '% coherencia discurso-acción según observación de campo',
            ],
        },
        high: {
            supervisor: [
                'Mentoring con un par supervisor de alto rendimiento en esta dimensión — observa cómo toma decisiones de seguridad bajo presión.',
                'Documenta tus recorridos de campo: qué observaste, qué corregiste, qué reconociste — la documentación refuerza el hábito.',
                'Cuando alguien de tu equipo reporta un riesgo, actúa antes de 24 horas — la velocidad de respuesta construye credibilidad.',
            ],
            manager: [
                'Acompañar al supervisor en un recorrido mensual — observar y retroalimentar su estilo de presencia en campo.',
                'Revisión del plan de visibilidad del supervisor: ¿está siendo ejecutado?',
            ],
            system: [
                'Facilitar espacios de aprendizaje entre pares supervisores sobre gestión de la tensión seguridad/producción.',
                'Incluir liderazgo visible como competencia en el ciclo de evaluación de desempeño.',
            ],
            kpi: [
                '# recorridos de campo',
                '% respuestas a reportes de riesgo en ≤24h',
                'Feedback del equipo sobre presencia del supervisor',
            ],
        },
    },
    D8: {
        critical: {
            supervisor: [
                'Revisa la planilla de turnos de tu área de los últimos 30 días — ¿quién lleva más horas continuas? ¿quién ha tenido menos descanso? Eso es un riesgo que tú gestionas.',
                "Antes de asignar una tarea de alto riesgo esta semana, pregúntale explícitamente a la persona: '¿Cómo estás? ¿Estás en condiciones de hacer esto de forma segura?' — hazlo tuyo, no como protocolo sino como hábito.",
                'Identifica las señales de fatiga en tu equipo: cambios de humor, errores frecuentes, respuestas lentas, aislamiento — y actúa antes de que causen un incidente.',
                'Si alguien muestra señales claras de fatiga o malestar, retíralo de la tarea crítica y gestiona el relevo — esa es tu responsabilidad como supervisor.',
                'Reporta a tu jefe si la carga de trabajo del área no permite descansos adecuados — esa presión no puede resolverse solo desde tu nivel.',
            ],
            manager: [
                'Auditoría inmediata de horas extras y rotaciones del área — si el promedio de horas continuas supera el límite definido, es un problema que debes resolver ahora.',
                'Revisión de la última correlación entre turnos de alta fatiga y accidentes o casi-accidentes del área.',
                'Pregunta directa al supervisor: ¿Tiene el equipo los relevos necesarios para que nadie trabaje en condiciones de fatiga extrema? Si no, es un problema de recursos.',
                'Garantizar que el supervisor tenga la autoridad real de retirar a una persona de una tarea por fatiga sin necesidad de justificación burocrática.',
            ],
            system: [
                'Revisión de política de gestión de fatiga: ¿existe? ¿tiene criterios claros para tareas de alto riesgo? ¿se aplica?',
                'Implementar límites máximos de horas continuas para tareas críticas con verificación activa.',
                'Protocolo de aptitud para el trabajo en tareas de alto riesgo — con criterios claros de qué verificar antes de asignar.',
                'Correlacionar datos de ausentismo, horas extras y accidentalidad del área — si hay correlación, es urgente una intervención sistémica.',
            ],
            kpi: [
                '# personas con jornadas extendidas',
                '% tareas críticas con verificación de aptitud',
                '# incidentes con fatiga como factor contribuyente',
                '# relevos gestionados por fatiga',
            ],
        },
        high: {
            supervisor: [
                'Incorpora la verificación de estado del equipo en tu rutina de inicio de turno — no como pregunta formal, como conversación.',
                'Gestiona pausas activas estructuradas en los momentos de mayor carga del turno — no esperes a que el equipo las pida.',
                'Redistribuye tareas cuando observes señales de fatiga — esa flexibilidad operativa es parte de tu rol.',
            ],
            manager: [
                'Revisar si el supervisor tiene autoridad y recursos para gestionar relevos cuando detecta fatiga.',
                'Monitorear horas extras y rotaciones del área mensualmente.',
            ],
            system: [
                'Programar pausas activas estructuradas en los turnos de mayor carga.',
                'Incorporar fatiga como factor de análisis en todas las investigaciones de incidentes del área.',
            ],
            kpi: [
                '# personas con jornada extendida',
                '# pausas activas realizadas',
                '# incidentes con fatiga como factor',
            ],
        },
    },
};

const normalizeText = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const extractCodeFromText = (value) => {
    const match = String(value || '').toUpperCase().match(/D[1-8]/);
    return match ? match[0] : null;
};

const inferDimensionCode = (key, title) => {
    const directCode = extractCodeFromText(key) || extractCodeFromText(title);
    if (directCode) return directCode;

    const normalized = normalizeText(`${key || ''} ${title || ''}`);
    return Object.entries(IPRA_DIMENSION_CATALOG).find(([, config]) => (
        config.keywords.some((keyword) => normalized.includes(normalizeText(keyword)))
    ))?.[0] || null;
};

const compareSummaryDisplayRows = (a, b) => {
    if (b.weightedContribution !== a.weightedContribution) {
        return b.weightedContribution - a.weightedContribution;
    }

    if (a.score !== b.score) {
        return a.score - b.score;
    }

    if (b.weightPercent !== a.weightPercent) {
        return b.weightPercent - a.weightPercent;
    }

    return a.summaryTitle.localeCompare(b.summaryTitle);
};

const buildBaseRows = (averageByDimension) => {
    return Object.entries(averageByDimension || {})
        .filter(([key]) => !String(key).toLowerCase().includes('general'))
        .map(([key, dimension]) => {
            const code = inferDimensionCode(key, dimension?.title);
            const config = code ? IPRA_DIMENSION_CATALOG[code] : null;
            const score = Number(dimension?.average);
            if (!code || !Number.isFinite(score)) return null;

            const tier = getSelfcare360DimensionTier(score);
            const tierMeta = IPRA_TIER_META[tier.key] || IPRA_TIER_META.low;
            const weightPercent = Number(getWeightPercent(dimension));
            const safeWeightPercent = Number.isFinite(weightPercent) ? weightPercent : 0;
            const contribution = roundHalfUp(score * (safeWeightPercent / 100), 1);
            const excludedFromIpra = Boolean(dimension?.validity?.exclude_from_ipra);

            return {
                key,
                code,
                sourceTitle: dimension?.title || config?.summaryTitle || code,
                summaryTitle: config?.summaryTitle || dimension?.title || code,
                planTitle: config?.planTitle || dimension?.title || code,
                score,
                weightPercent: safeWeightPercent,
                weightedContribution: Number.isFinite(contribution) ? contribution : 0,
                excludedFromIpra,
                tier,
                tierMeta,
                priorityLabel: tierMeta.priorityLabel,
                actionWindow: tierMeta.timeframe,
                detailPlanEligible: ['critical', 'high'].includes(tier.key),
            };
        })
        .filter(Boolean);
};

export const getIpraSummaryDisplayRows = (averageByDimension) => {
    return buildBaseRows(averageByDimension)
        .sort(compareSummaryDisplayRows);
};

export const getIpraInterventionSummaryRows = (averageByDimension) => {
    return getIpraSummaryDisplayRows(averageByDimension)
        .map((row, index) => ({
            ...row,
            priorityOrder: index + 1,
        }));
};

export const getIpraDetailedPlanRows = (averageByDimension) => {
    return getIpraInterventionSummaryRows(averageByDimension)
        .filter((row) => row.detailPlanEligible && INTERVENTION_PLANS[row.code]?.[row.tier.key])
        .slice(0, IPRA_INTERVENTION_PAGE_LIMIT);
};

export const getIpraInterventionPlan = (code, tierKey) => {
    return INTERVENTION_PLANS[code]?.[tierKey] || null;
};

const addDays = (baseDate, days) => {
    const nextDate = new Date(baseDate);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const formatActionPlanDueDate = (dateLike) => new Intl.DateTimeFormat('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
}).format(dateLike);

export const getIpraActionPlanPrefillRows = (averageByDimension, referenceDate = new Date()) => {
    return getIpraDetailedPlanRows(averageByDimension)
        .slice(0, 3)
        .map((row, index) => {
            const plan = getIpraInterventionPlan(row.code, row.tier?.key);
            const primaryAction = plan?.supervisor?.[0];

            if (!primaryAction) return null;

            return {
                id: row.code,
                title: `Acción prioritaria ${index + 1}`,
                dimensionLabel: `${row.code} ${row.summaryTitle}`,
                action: primaryAction,
                dueDate: index === 0 ? formatActionPlanDueDate(addDays(referenceDate, 15)) : '',
            };
        })
        .filter(Boolean);
};

export const formatCompactPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0%';
    return Number.isInteger(numeric) ? `${numeric}%` : `${numeric.toFixed(1)}%`;
};

export const getIpraPriorityNarrative = (rows) => {
    if (!rows || rows.length === 0) {
        return 'Todas las dimensiones evaluadas se encuentran en nivel moderado o bajo. El foco del plan es sostener buenas prácticas, monitorear la consistencia operacional y evitar regresiones en el corto plazo.';
    }

    const labels = rows.map((row) => `${row.code} ${row.summaryTitle}`);
    const listText = labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`;

    return `Las prioridades del plan se concentran en ${listText}. El orden responde al mayor aporte dimensional, luego al menor score y finalmente al mayor peso, para intervenir primero donde el resultado tiene más impacto.`;
};
